import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { parivartanAPI } from '../../../services/parivartanAPI';
import { platformAPI } from '../../../services/platformAPI';
import { userHasPermission } from '../../../utils/permissions';
import { useWorkspaceAccess } from '../../../hooks/useWorkspaceAccess';
import './TenderCommercialsTab.scss';
import { useAdminRecordDelete } from '../../../components/workspace/useAdminRecordDelete';

const inr = (paise) => {
  if (paise == null || Number.isNaN(Number(paise))) return 'Uncosted';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(paise) / 100);
};

const blankLine = () => ({
  description: '',
  designation: '',
  costHeadCode: '',
  costHeadCategory: '',
  rateBasis: 'monthly',
  quantity: '',
  rateAmount: '',
  assumption: ''
});

const SAMPLE = {
  assumptions: 'Person-months are full months on site. The lump sum is out-of-pocket expenses.',
  exclusions: 'GST and client-provided facilities are outside this estimate.',
  contingencyPercent: 0,
  overheadPercent: 0,
  pricingMethod: 'gross_margin',
  targetGrossMarginPercent: 20,
  markupPercent: 20,
  taxPercent: 18,
  lines: [
    { description: 'Team leader', designation: 'Team leader', rateBasis: 'monthly', quantity: 12, rateAmount: 150000, assumption: '12 person-months' },
    { description: 'Resident engineer', designation: 'Resident engineer', rateBasis: 'monthly', quantity: 24, rateAmount: 95000, assumption: '24 person-months' },
    { description: 'Out of pocket', designation: '', rateBasis: 'lump_sum', quantity: 1, rateAmount: 650000, assumption: 'Lump sum' }
  ]
};

const canRole = (user, permission, roles) => {
  if (userHasPermission(user?.permissions, permission)) return true;
  return (user?.roles || []).some((role) => roles.includes(String(role || '').trim().toUpperCase()));
};

const lineQuantity = (line) => {
  if (line.rateBasis === 'hourly') return line.hours ?? line.quantity ?? '';
  if (line.rateBasis === 'monthly') return line.durationMonths ?? line.quantity ?? '';
  return line.quantity ?? '';
};

const fromEstimate = (estimate) => ({
  assumptions: estimate?.assumptions || '',
  exclusions: estimate?.exclusions || '',
  contingencyPercent: estimate?.contingencyPercent ?? 0,
  overheadPercent: estimate?.overheadPercent ?? 0,
  pricingMethod: estimate?.pricingMethod || 'gross_margin',
  targetGrossMarginPercent: estimate?.targetGrossMarginPercent ?? 0,
  markupPercent: estimate?.markupPercent ?? 0,
  taxPercent: estimate?.taxPercent ?? 0,
  overheadLinesAreDistinct: estimate?.overheadTreatment === 'distinct',
  overheadDistinctNote: estimate?.overheadDistinctNote || '',
  lines: (estimate?.lines || []).map((line) => ({
    description: line.description || '',
    designation: line.designation || '',
    costHeadCode: line.costHeadCode || '',
    costHeadCategory: line.costHeadCategory || '',
    rateBasis: line.rateBasis || 'monthly',
    quantity: lineQuantity(line),
    rateAmount: line.rateAmount != null && line.rateAmount !== ''
      ? line.rateAmount
      : (line.uncosted || line.rateAmountPaise == null ? '' : line.rateAmountPaise / 100),
    assumption: line.assumption || ''
  }))
});

const toPayload = (form) => ({
  assumptions: form.assumptions,
  exclusions: form.exclusions,
  contingencyPercent: Number(form.contingencyPercent || 0),
  overheadPercent: Number(form.overheadPercent || 0),
  pricingMethod: form.pricingMethod,
  targetGrossMarginPercent: Number(form.targetGrossMarginPercent || 0),
  markupPercent: Number(form.markupPercent || 0),
  taxPercent: Number(form.taxPercent || 0),
  overheadLinesAreDistinct: form.overheadLinesAreDistinct === true,
  overheadDistinctNote: form.overheadDistinctNote || '',
  lines: form.lines.map((line) => {
    const quantity = line.quantity === '' ? null : Number(line.quantity);
    return {
      description: line.description,
      designation: line.designation,
      category: line.designation || line.description || 'Effort',
      costHeadCode: line.costHeadCode || '',
      costHeadCategory: line.costHeadCategory || '',
      rateBasis: line.rateBasis,
      quantity: line.rateBasis === 'lump_sum' ? (quantity || 1) : quantity,
      durationMonths: line.rateBasis === 'monthly' ? quantity : undefined,
      hours: line.rateBasis === 'hourly' ? quantity : undefined,
      rateAmount: line.rateAmount === '' ? null : Number(line.rateAmount),
      assumption: line.assumption
    };
  })
});

const TenderCommercialsTab = ({ tenderId }) => {
  const { allowed, ready } = useWorkspaceAccess();
  const canRead = ready && allowed(['tender.costing.read']);
  const user = useSelector((state) => state.auth.user);
  const canEdit = canRole(user, 'tender.costing.edit', ['PLATFORM ADMINISTRATOR', 'TENDER ADMINISTRATOR', 'BID MANAGER', 'COMMERCIAL REVIEWER']);
  const canApprove = canRole(user, 'tender.costing.approve', ['PLATFORM ADMINISTRATOR', 'TENDER ADMINISTRATOR', 'COMMERCIAL REVIEWER', 'BID APPROVER']);
  const [loading, setLoading] = useState(true);
  const [estimates, setEstimates] = useState([]);
  const [offers, setOffers] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState(fromEstimate(null));
  const [offerAmount, setOfferAmount] = useState('');
  const [offerRef, setOfferRef] = useState('');
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [costHeads, setCostHeads] = useState([]);

  const load = useCallback(async () => {
    const response = await parivartanAPI.listCostEstimates(tenderId);
    const rows = response.data?.data?.estimates || [];
    setEstimates(rows);
    setOffers(response.data?.data?.offers || []);
    setSelectedId((current) => current || rows[0]?._id || '');
    return rows;
  }, [tenderId]);

  useEffect(() => {
    if (!canRead) {
      setEstimates([]);
      setOffers([]);
      setForm(fromEstimate(null));
      setLoading(false);
      return undefined;
    }
    load().catch(() => setMessage({ variant: 'danger', text: 'Unable to load the commercial estimate.' })).finally(() => setLoading(false));
    return undefined;
  }, [load, canRead]);

  useEffect(() => {
    if (!canRead) return undefined;
    const keep = (form.lines || []).map((line) => line.costHeadCode).filter(Boolean).join(',');
    platformAPI.listReference('cost-heads', { keep }).then((response) => {
      setCostHeads(response.data?.data?.values || []);
    }).catch(() => setCostHeads([]));
    return undefined;
  }, [canRead, form.lines]);

  const selected = estimates.find((row) => row._id === selectedId) || null;
  const editable = selected && (selected.status === 'draft' || selected.status === 'returned') && canEdit;
  const approved = estimates.find((row) => row.status === 'approved');

  useEffect(() => {
    if (selected) setForm(fromEstimate(selected));
  }, [selected]);

  const setLine = (index, key, value) => {
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line, i) => (i === index ? { ...line, [key]: value } : line))
    }));
  };

  const preview = useMemo(() => {
    const lines = form.lines.map((line) => {
      const rate = line.rateAmount === '' ? null : Number(line.rateAmount);
      const qty = Number(line.quantity || 0);
      if (rate == null || Number.isNaN(rate)) return { uncosted: true, cost: null };
      return { uncosted: false, cost: Math.round(rate * qty) };
    });
    const normal = form.lines.reduce((sum, line, index) => (
      line.costHeadCategory === 'overhead' || lines[index].uncosted ? sum : sum + (lines[index].cost || 0)
    ), 0);
    const overheadBase = form.lines.reduce((sum, line, index) => (
      line.costHeadCategory === 'overhead' && !lines[index].uncosted ? sum + (lines[index].cost || 0) : sum
    ), 0);
    const percent = Number(form.overheadPercent || 0);
    const both = percent > 0 && overheadBase > 0;
    const distinct = form.overheadLinesAreDistinct === true && String(form.overheadDistinctNote || '').trim();
    const contingency = 1 + Number(form.contingencyPercent || 0) / 100;
    const afterPercent = 1 + (percent > 0 ? percent / 100 : 0);
    const blocked = both && !distinct;
    const cost = blocked
      ? null
      : both
        ? Math.round((normal * contingency * afterPercent) + (overheadBase * contingency))
        : overheadBase > 0
          ? Math.round((normal + overheadBase) * contingency)
          : Math.round(normal * contingency * afterPercent);
    const margin = Number(form.targetGrossMarginPercent || 0);
    const markup = Number(form.markupPercent || 0);
    const gross = cost == null || margin < 0 || margin >= 100 ? null : Math.round(cost / (1 - margin / 100));
    const marked = cost == null || markup < 0 || markup > 500 ? null : Math.round(cost * (1 + markup / 100));
    const quote = form.pricingMethod === 'markup' ? marked : gross;
    const tax = quote == null ? null : Math.round(quote * (Number(form.taxPercent || 0) / 100));
    return {
      lines,
      cost,
      gross,
      marked,
      quote,
      tax,
      blocked,
      uncosted: lines.filter((line) => line.uncosted).length
    };
  }, [form]);

  const run = async (work) => {
    setBusy(true);
    setMessage(null);
    try {
      await work();
      await load();
    } catch (error) {
      setMessage({ variant: 'danger', text: error.response?.data?.message || 'The commercial estimate could not be saved.' });
    } finally {
      setBusy(false);
    }
  };

  const save = () => run(async () => {
    if (!selected) {
      const response = await parivartanAPI.createCostEstimate(tenderId, toPayload(form));
      setSelectedId(response.data?.data?.estimate?._id || '');
      setMessage({ variant: 'success', text: 'Draft estimate saved. Earlier approved versions stay as they were.' });
      return;
    }
    await parivartanAPI.updateCostEstimate(selected._id, toPayload(form));
    setMessage({ variant: 'success', text: 'Draft saved.' });
  });

  const admin = useAdminRecordDelete({
    entity: 'estimate',
    noun: 'estimate',
    plural: 'estimates',
    reload: load,
    label: (row) => `Version ${row.version}`,
    note: 'A submitted or approved estimate stays. A draft that is not used on a financial offer can be removed.'
  });

  if (!ready) return <Spinner animation="border" size="sm" />;
  if (!canRead) {
    return <Alert variant="warning">This area is restricted. Commercial estimates, rates, and worked examples are hidden.</Alert>;
  }

  if (loading) return <Spinner animation="border" size="sm" />;

  return (
    <div className="tcom">
      {message ? <Alert variant={message.variant}>{message.text}</Alert> : null}
      {admin.notice}
      <div className="tcom__layout">
        <aside className="tcom__versions">
          <div className="tcom__head">
            <h3>Versions</h3>
            {admin.enabled ? (
              <div className="d-flex gap-2">
                <Button size="sm" variant="outline-danger" disabled={!selected} onClick={() => admin.ask('one', [selected._id], [`Version ${selected.version}`])}>Delete version</Button>
                <Button size="sm" variant="outline-danger" disabled={!estimates.length} onClick={() => admin.ask('all', estimates.map((row) => row._id), estimates.map((row) => `Version ${row.version}`))}>Delete all</Button>
              </div>
            ) : null}
            {canEdit ? (
              <Button size="sm" variant="primary" disabled={busy} onClick={() => run(async () => {
                const response = await parivartanAPI.createCostEstimate(tenderId, { lines: [], pricingMethod: 'gross_margin' });
                setSelectedId(response.data?.data?.estimate?._id || '');
                setMessage({ variant: 'success', text: 'A new revision was opened. The approved version is unchanged.' });
              })}
              >
                New revision
              </Button>
            ) : null}
          </div>
          {estimates.length ? estimates.map((row) => (
            <button
              key={row._id}
              type="button"
              className={`tcom__version${row._id === selectedId ? ' is-active' : ''}`}
              onClick={() => setSelectedId(row._id)}
            >
              <strong>v{row.version}</strong>
              <span>{row.status}</span>
              <small>{inr(row.indicativePricePaise)}</small>
            </button>
          )) : <p>No estimate yet.</p>}
        </aside>

        <section className="tcom__sheet">
          <header className="tcom__sheet-head">
            <div>
              <p>Commercial estimate</p>
              <h3>{selected ? `Version ${selected.version}` : 'New estimate'}</h3>
            </div>
            {canEdit && (!selected || editable) ? (
              <div className="tcom__actions">
                <Button size="sm" variant="outline-primary" type="button" disabled={busy} onClick={() => setForm(fromEstimate(SAMPLE))}>
                  Worked example
                </Button>
                <Button size="sm" variant="primary" type="button" disabled={busy || preview.blocked} onClick={save}>Save draft</Button>
                {selected && editable ? (
                  <Button size="sm" variant="outline-primary" type="button" disabled={busy} onClick={() => run(async () => {
                    await parivartanAPI.updateCostEstimate(selected._id, toPayload(form));
                    await parivartanAPI.submitCostEstimate(selected._id, {});
                    setMessage({ variant: 'success', text: 'Submitted for commercial approval.' });
                  })}
                  >
                    Submit
                  </Button>
                ) : null}
              </div>
            ) : null}
          </header>

          {selected?.status === 'returned' && selected.returnComments ? (
            <Alert variant="warning">Returned: {selected.returnComments}</Alert>
          ) : null}
          {selected?.status === 'approved' ? (
            <Alert variant="success">Approved. The rates on this version are pinned. A later change to the rate master does not change this version.</Alert>
          ) : null}

          <div className="tcom__lines">
            {(editable || !selected ? form.lines : selected.lines).map((line, index) => {
              const read = !(editable || !selected);
              const quantity = read ? lineQuantity(line) : line.quantity;
              const rate = read ? (line.uncosted || line.rateAmountPaise == null ? '' : line.rateAmountPaise / 100) : line.rateAmount;
              const cost = read ? line.lineCostPaise : (preview.lines[index]?.cost == null ? null : preview.lines[index].cost * 100);
              return (
                <div className="tcom__line" key={line._id || index}>
                  <Form.Select disabled={read} aria-label="Cost head" value={line.costHeadCode || ''} onChange={(event) => {
                    const head = costHeads.find((row) => row.code === event.target.value);
                    setForm((current) => ({
                      ...current,
                      lines: current.lines.map((item, itemIndex) => (itemIndex === index ? {
                        ...item,
                        costHeadCode: event.target.value,
                        costHeadCategory: head?.meta?.category || '',
                        rateBasis: head?.meta?.category === 'personnel'
                          ? (item.rateBasis === 'hourly' || item.rateBasis === 'monthly' ? item.rateBasis : 'monthly')
                          : (head?.meta?.basis || item.rateBasis),
                        description: item.description || head?.label || ''
                      } : item))
                    }));
                  }}>
                    <option value="">No cost head</option>
                    {costHeads.filter((row) => row.status === 'active' || row.code === line.costHeadCode).map((row) => (
                      <option key={row.code} value={row.code}>{row.label}{row.status === 'inactive' ? ' (inactive)' : ''}</option>
                    ))}
                  </Form.Select>
                  <Form.Control disabled={read} placeholder="Description" value={line.description || ''} onChange={(event) => setLine(index, 'description', event.target.value)} />
                  <Form.Control disabled={read} placeholder="Designation" value={line.designation || ''} onChange={(event) => setLine(index, 'designation', event.target.value)} />
                  <Form.Select disabled={read} value={line.rateBasis} onChange={(event) => setLine(index, 'rateBasis', event.target.value)}>
                    <option value="monthly">Person-month</option>
                    <option value="hourly">Hour</option>
                    <option value="lump_sum">Lump sum</option>
                    <option value="unit">Unit</option>
                  </Form.Select>
                  <Form.Control disabled={read} placeholder="Qty" value={quantity} onChange={(event) => setLine(index, 'quantity', event.target.value)} />
                  <Form.Control disabled={read} placeholder="Rate (blank = uncosted)" value={rate} onChange={(event) => setLine(index, 'rateAmount', event.target.value)} />
                  <strong>{line.uncosted || cost == null ? 'Uncosted' : inr(cost)}</strong>
                  {(editable || !selected) ? (
                    <button type="button" className="tcom__remove" onClick={() => setForm((current) => ({ ...current, lines: current.lines.filter((_, i) => i !== index) }))}>Remove</button>
                  ) : null}
                </div>
              );
            })}
          </div>
          {(editable || !selected) ? (
            <Button size="sm" variant="outline-primary" type="button" onClick={() => setForm((current) => ({ ...current, lines: [...current.lines, blankLine()] }))}>
              Add line
            </Button>
          ) : null}

          <div className="tcom__pricing">
            <label>
              Assumptions
              <Form.Control as="textarea" rows={2} disabled={!(editable || !selected)} value={editable || !selected ? form.assumptions : selected.assumptions || ''} onChange={(event) => setForm({ ...form, assumptions: event.target.value })} />
            </label>
            <label>
              Exclusions
              <Form.Control as="textarea" rows={2} disabled={!(editable || !selected)} value={editable || !selected ? form.exclusions : selected.exclusions || ''} onChange={(event) => setForm({ ...form, exclusions: event.target.value })} />
            </label>
            <label>
              Pricing
              <Form.Select disabled={!(editable || !selected)} value={form.pricingMethod} onChange={(event) => setForm({ ...form, pricingMethod: event.target.value })}>
                <option value="gross_margin">Gross margin</option>
                <option value="markup">Markup</option>
              </Form.Select>
            </label>
            <label>
              Gross margin %
              <Form.Control disabled={!(editable || !selected)} value={form.targetGrossMarginPercent} onChange={(event) => setForm({ ...form, targetGrossMarginPercent: event.target.value })} />
            </label>
            <label>
              Markup %
              <Form.Control disabled={!(editable || !selected)} value={form.markupPercent} onChange={(event) => setForm({ ...form, markupPercent: event.target.value })} />
            </label>
            <label>
              Contingency %
              <Form.Control disabled={!(editable || !selected)} value={form.contingencyPercent} onChange={(event) => setForm({ ...form, contingencyPercent: event.target.value })} />
            </label>
            <label>
              Overhead %
              <Form.Control disabled={!(editable || !selected)} value={form.overheadPercent} onChange={(event) => setForm({ ...form, overheadPercent: event.target.value })} />
            </label>
            <label>
              Tax % <span>kept separate from cost</span>
              <Form.Control disabled={!(editable || !selected)} value={form.taxPercent} onChange={(event) => setForm({ ...form, taxPercent: event.target.value })} />
            </label>
          </div>

          {(editable || !selected) && Number(form.overheadPercent || 0) > 0 && form.lines.some((line) => line.costHeadCategory === 'overhead' && line.rateAmount !== '') ? (
            <Form.Check
              className="mt-3"
              id="overhead-distinct"
              checked={form.overheadLinesAreDistinct === true}
              onChange={(event) => setForm({ ...form, overheadLinesAreDistinct: event.target.checked })}
              label="This overhead cost-head line is a different cost from the overhead percent. Leave this clear to keep the two as alternatives."
            />
          ) : null}
          {(editable || !selected) && form.overheadLinesAreDistinct ? (
            <Form.Group className="mt-2">
              <Form.Label>Why this line is a different cost</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.overheadDistinctNote || ''}
                onChange={(event) => setForm({ ...form, overheadDistinctNote: event.target.value })}
                placeholder="Required. This explanation is kept with the estimate."
              />
            </Form.Group>
          ) : null}
          {selected && !(editable || !selected) && selected.overheadDistinctNote ? (
            <p className="tcom__note">Distinct overhead: {selected.overheadDistinctNote}</p>
          ) : null}
          {preview.blocked ? (
            <Alert variant="warning" className="mt-3 mb-0">The overhead percent and an overhead cost-head line both record overhead. Use one of them, or confirm that the line is a different cost. The total is not calculated until that choice is made.</Alert>
          ) : null}

          <dl className="tcom__totals">
            <div><dt>Cost</dt><dd>{selected && !(editable || !selected) ? (selected.uncostedLineCount > 0 ? `Incomplete · ${selected.uncostedLineCount} without a rate` : inr(selected.totalCostPaise)) : (preview.blocked ? 'Not calculated' : preview.uncosted > 0 ? `Incomplete · ${preview.uncosted} without a rate` : inr(preview.cost * 100))}</dd></div>
            <div><dt>20% style check</dt><dd>Gross margin {inr((preview.gross || 0) * 100)} · Markup {inr((preview.marked || 0) * 100)}</dd></div>
            <div><dt>Quote</dt><dd>{preview.quote == null ? 'Margin out of bounds' : inr(preview.quote * 100)}</dd></div>
            <div><dt>Tax</dt><dd>{preview.tax == null ? '—' : inr(preview.tax * 100)}</dd></div>
            <div><dt>Uncosted lines</dt><dd>{preview.uncosted}</dd></div>
          </dl>
          <p className="tcom__note">A 20% gross margin on ₹47,30,000 is ₹59,12,500. The same 20% taken as markup is ₹56,76,000. Gross margin at 100% is rejected.</p>
          <p className="tcom__note">Overhead examples, with no contingency: ₹1,000 of effort and 10% overhead is ₹1,100. ₹1,000 of effort and a ₹50 overhead cost head is ₹1,050. Both together are saved only when the ₹50 line is confirmed as a different cost and the reason is written on the estimate. The total is then ₹1,150. A personnel line can stay hourly or monthly. A missing rate stays uncosted.</p>

          {selected?.status === 'submitted' && canApprove && String(selected.submittedBy) !== String(user?._id || user?.id) ? (
            <div className="tcom__actions">
              <Button size="sm" variant="primary" disabled={busy} onClick={() => run(async () => {
                await parivartanAPI.approveCostEstimate(selected._id, { comments: 'Approved' });
                setMessage({ variant: 'success', text: 'Approved. Applied rates are now pinned to this version.' });
              })}
              >
                Approve
              </Button>
              <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => run(async () => {
                await parivartanAPI.returnCostEstimate(selected._id, { comments: 'Please revise the estimate.' });
                setMessage({ variant: 'success', text: 'Returned to the preparer. This version stays independent of any approved revision.' });
              })}
              >
                Return
              </Button>
            </div>
          ) : null}
        </section>
      </div>

      <section className="tcom__offer">
        <h3>Financial offer</h3>
        <p>The authority-format offer is linked to the approved commercial version. Confirmation is allowed only when the offer total matches that quote.</p>
        {approved ? <p>Approved version v{approved.version} · quote {inr(approved.indicativePricePaise)}</p> : <p>No approved version yet.</p>}
        {offers.map((offer) => (
          <div key={offer._id} className="tcom__offer-row">
            <span>{offer.reference || 'Offer'} · v{offer.estimateVersion} · {inr(offer.offerTotalPaise)} · {offer.status}</span>
            {offer.status !== 'confirmed' && canApprove ? (
              <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => run(async () => {
                await parivartanAPI.confirmFinancialOffer(offer._id);
                setMessage({ variant: 'success', text: 'The offer total agrees with the approved commercial version.' });
              })}
              >
                Confirm agreement
              </Button>
            ) : null}
          </div>
        ))}
        {approved && canEdit ? (
          <div className="tcom__offer-form">
            <Form.Control placeholder="Offer reference" value={offerRef} onChange={(event) => setOfferRef(event.target.value)} />
            <Form.Control placeholder="Offer total (INR)" value={offerAmount} onChange={(event) => setOfferAmount(event.target.value)} />
            <Button size="sm" variant="primary" disabled={busy} onClick={() => run(async () => {
              await parivartanAPI.saveFinancialOffer(tenderId, {
                estimateId: approved._id,
                amount: Number(offerAmount),
                reference: offerRef
              });
              setMessage({ variant: 'success', text: 'Offer linked to the approved version. A reviewer confirms the total.' });
            })}
            >
              Link offer
            </Button>
          </div>
        ) : null}
      </section>
      {admin.modal}
    </div>
  );
};

export default TenderCommercialsTab;
